import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import ConfirmationModal from '../Common/ConfirmationModal';
import { customersApi } from '../../services/dispatchApi';
import { EmptyState, LoadingState } from '../Common/PanelState';

const FLARE_RED = '#c4151c';
const FLARE_RED_DARK = '#9f1016';
const PAGE_SIZE = 30;
const Icon = ({ path, className = 'h-5 w-5' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={path} />
  </svg>
);

function getInitials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return 'C';
  return ((parts[0][0] || '') + (parts[1]?.[0] || '')).toUpperCase();
}

function StatCard({ label, value, hint, icon, iconBg, valueClass = 'text-gray-900' }) {
  return (
    <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100">
      <div className="flex items-center gap-3">
        <div
          className="w-11 h-11 rounded-lg flex items-center justify-center text-white shadow-sm shrink-0"
          style={{ backgroundColor: iconBg }}
        >
          <Icon path={icon} className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <div className="text-xs font-semibold uppercase text-gray-500 tracking-wide">{label}</div>
          <div className={`text-2xl font-extrabold leading-tight ${valueClass}`}>{value}</div>
          {hint ? <div className="text-xs text-gray-400 mt-0.5">{hint}</div> : null}
        </div>
      </div>
    </div>
  );
}

function statusBadge(status) {
  if (status === 'banned') return 'bg-rose-50 text-rose-800 border-rose-100';
  if (status === 'inactive') return 'bg-slate-50 text-slate-700 border-slate-200';
  return 'bg-emerald-50 text-emerald-800 border-emerald-100';
}

function statusLabel(status) {
  if (status === 'banned') return 'Banned';
  if (status === 'inactive') return 'Inactive';
  return 'Active';
}

const emptyForm = { name: '', email: '', phone: '', password: '', address: '', city: 'Lahore', source: 'Admin' };

export default function MagazineUserManagementSection() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [deleting, setDeleting] = useState(null);
  const [banning, setBanning] = useState(null);

  const load = async () => {
    setLoading(true);
    setLoadError('');
    try {
      const { data } = await customersApi.list({ per_page: 200 });
      const rows = data?.data || data || [];
      setList(Array.isArray(rows) ? rows : []);
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || 'Failed to load users';
      setLoadError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const stats = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0); const ts = today.getTime();
    return {
      total: list.length,
      active: list.filter((u) => u.status !== 'banned' && u.status !== 'inactive').length,
      banned: list.filter((u) => u.status === 'banned').length,
      today: list.filter((u) => new Date(u.created_at || 0).getTime() >= ts).length,
      newsletter: list.filter((u) => u.newsletter_optin).length,
    };
  }, [list]);

  const sources = useMemo(() => {
    const s = new Set();
    list.forEach((u) => { if (u.source) s.add(u.source); });
    return ['all', ...Array.from(s)];
  }, [list]);

  const filtered = useMemo(() => {
    let l = list;
    if (statusFilter === 'active') l = l.filter((u) => u.status !== 'banned' && u.status !== 'inactive');
    if (statusFilter === 'banned') l = l.filter((u) => u.status === 'banned');
    if (sourceFilter !== 'all') l = l.filter((u) => u.source === sourceFilter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      l = l.filter((u) =>
        (u.name || '').toLowerCase().includes(q) ||
        (u.email || '').toLowerCase().includes(q) ||
        (u.phone || '').toLowerCase().includes(q) ||
        (u.city || '').toLowerCase().includes(q) ||
        (u.source || '').toLowerCase().includes(q)
      );
    }
    return l;
  }, [list, statusFilter, sourceFilter, search]);

  const lastPage = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, lastPage);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const filtersActive = Boolean(statusFilter !== 'all' || sourceFilter !== 'all' || search.trim());
  const clearFilters = () => {
    setStatusFilter('all');
    setSourceFilter('all');
    setSearch('');
    setPage(1);
  };

  const openAdd = () => { setEditing(null); setForm(emptyForm); setAdding(true); };
  const openEdit = (u) => {
    setEditing(u);
    setAdding(true);
    setForm({
      name: u.name || '',
      email: u.email || '',
      phone: u.phone || '',
      password: '',
      address: u.address || '',
      city: u.city || 'Lahore',
      source: u.source || 'Admin',
    });
  };
  const closeForm = () => { setAdding(false); setEditing(null); setForm(emptyForm); };

  const submitForm = async (e) => {
    e.preventDefault();
    try {
      if (!editing) {
        if (!form.email) { toast.warn('Email is required'); return; }
        if (!form.password || String(form.password).length < 6) { toast.warn('Password min 6 chars'); return; }
        await customersApi.register({ ...form });
        toast.success('Customer created');
      } else {
        const payload = {
          name: form.name,
          email: form.email,
          phone: form.phone,
          address: form.address,
          city: form.city,
          source: form.source,
        };
        if (form.password && form.password.length >= 6) payload.password = form.password;
        await customersApi.update(editing.id, payload);
        toast.success('Customer updated');
      }
      closeForm();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || 'Save failed');
    }
  };

  const confirmDeleteOne = async () => {
    if (!deleting) return;
    await customersApi.remove(deleting.id);
    toast.success('Deleted');
    setDeleting(null);
    load();
  };

  const toggleBan = async () => {
    if (!banning) return;
    const banned = banning.status === 'banned';
    await customersApi.update(banning.id, { status: banned ? 'active' : 'banned' });
    toast.success(banned ? 'Unbanned' : 'Banned');
    setBanning(null);
    load();
  };

  return (
    <div className="space-y-6">
      <ConfirmationModal isOpen={!!deleting} onClose={() => setDeleting(null)} onConfirm={confirmDeleteOne} title="Delete User">
        Delete <strong>{deleting?.name || deleting?.email}</strong>?
      </ConfirmationModal>
      <ConfirmationModal isOpen={!!banning} onClose={() => setBanning(null)} onConfirm={toggleBan} title={banning?.status === 'banned' ? 'Unban' : 'Ban'}>
        {banning?.status === 'banned' ? 'Restore access?' : 'Ban this customer?'}
      </ConfirmationModal>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="admin-page-title">User Management</h1>
          <p className="admin-page-sub">Storefront customers stored in MySQL. Ban, edit, or add accounts without touching staff users.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 text-gray-800 rounded-lg shadow-sm hover:bg-gray-50 font-semibold text-sm"
          >
            <Icon path="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" className="h-4 w-4" />
            Refresh
          </button>
          <button
            type="button"
            onClick={openAdd}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-white font-bold shadow-lg"
            style={{ backgroundColor: FLARE_RED }}
          >
            <Icon path="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" className="h-5 w-5" />
            Add Customer
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard
          label="Total"
          value={stats.total}
          hint="All customers"
          icon="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
          iconBg={FLARE_RED}
          valueClass="text-gray-900"
        />
        <StatCard
          label="Active"
          value={stats.active}
          hint="Can sign in"
          icon="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
          iconBg="#059669"
        />
        <StatCard
          label="Banned"
          value={stats.banned}
          hint="Access blocked"
          icon="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636"
          iconBg="#dc2626"
          valueClass={stats.banned ? 'text-rose-700' : 'text-gray-900'}
        />
        <StatCard
          label="Today"
          value={stats.today}
          hint="Registered today"
          icon="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
          iconBg="#7c3aed"
        />
        <StatCard
          label="Newsletter"
          value={stats.newsletter}
          hint="Opted in"
          icon="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
          iconBg="#2563eb"
        />
      </div>

      {adding && (
        <form onSubmit={submitForm} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-4">
          <div>
            <h2 className="text-sm font-bold text-gray-900">{editing ? 'Edit Customer' : 'Add Customer'}</h2>
            <p className="text-xs text-gray-500 mt-0.5">{editing ? 'Leave password blank to keep the current one.' : 'Email and a password of at least 6 characters are required.'}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="text-xs font-semibold text-gray-600">Name</label>
              <input required placeholder="Full name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="w-full mt-1 p-2.5 text-sm border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400 bg-white" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600">Email</label>
              <input required type="email" placeholder="name@domain.com" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className="w-full mt-1 p-2.5 text-sm border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400 bg-white" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600">Phone</label>
              <input placeholder="+92 300 1234567" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} className="w-full mt-1 p-2.5 text-sm border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400 bg-white" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600">{editing ? 'Password (optional)' : 'Password'}</label>
              <input type="password" placeholder={editing ? 'Leave blank to keep' : 'Min 6 characters'} value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} className="w-full mt-1 p-2.5 text-sm border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400 bg-white" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600">City</label>
              <input placeholder="Lahore" value={form.city} onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))} className="w-full mt-1 p-2.5 text-sm border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400 bg-white" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600">Source</label>
              <input placeholder="Admin" value={form.source} onChange={(e) => setForm((f) => ({ ...f, source: e.target.value }))} className="w-full mt-1 p-2.5 text-sm border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400 bg-white" />
            </div>
            <div className="md:col-span-3">
              <label className="text-xs font-semibold text-gray-600">Address</label>
              <textarea placeholder="Street, area" value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} className="w-full mt-1 p-2.5 text-sm border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400 bg-white" rows={2} />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={closeForm} className="px-4 py-2.5 bg-white border border-gray-200 text-gray-800 rounded-lg shadow-sm hover:bg-gray-50 font-semibold text-sm">Cancel</button>
            <button type="submit" className="px-5 py-2.5 text-white rounded-lg font-bold shadow" style={{ backgroundColor: FLARE_RED }}>{editing ? 'Save' : 'Create'}</button>
          </div>
        </form>
      )}

      <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="text-xs font-semibold text-gray-600">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              className="w-full mt-1 text-sm border border-gray-300 rounded-lg p-2 outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400 bg-white"
            >
              <option value="all">All statuses</option>
              <option value="active">Active</option>
              <option value="banned">Banned</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600">Source</label>
            <select
              value={sourceFilter}
              onChange={(e) => { setSourceFilter(e.target.value); setPage(1); }}
              className="w-full mt-1 text-sm border border-gray-300 rounded-lg p-2 outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400 bg-white"
            >
              {sources.map((s) => <option key={s} value={s}>{s === 'all' ? 'All sources' : s}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600">Search</label>
            <input
              type="search"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Name, email, phone, city…"
              className="w-full mt-1 text-sm border border-gray-300 rounded-lg p-2 outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400"
            />
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <div
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold"
            style={{ backgroundColor: '#fff1f2', color: FLARE_RED_DARK, border: '1px solid rgba(196,21,28,0.15)' }}
          >
            <Icon path="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" className="h-3.5 w-3.5" />
            {filtered.length} matching
          </div>
          {filtersActive ? (
            <button
              type="button"
              onClick={clearFilters}
              className="ml-1 px-2.5 py-1 rounded-md bg-white border border-gray-200 text-xs font-semibold text-gray-700 hover:bg-gray-50"
            >
              Clear filters
            </button>
          ) : null}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100">
        <div className="px-5 py-3.5 border-b border-gray-100 bg-gray-50 flex items-center justify-between gap-3">
          <div>
            <div className="text-sm font-bold text-gray-900">Customer list</div>
            <div className="text-xs text-gray-500 mt-0.5">
              {loading ? 'Updating…' : `${filtered.length} ${filtered.length === 1 ? 'customer' : 'customers'} matching`}
            </div>
          </div>
        </div>
        {loading ? (
          <LoadingState label="Loading customers" />
        ) : loadError && list.length === 0 ? (
          <EmptyState title="Could not load customers" hint={loadError}>
            <button
              type="button"
              onClick={load}
              className="px-6 py-2.5 rounded-lg text-white font-bold shadow"
              style={{ backgroundColor: FLARE_RED }}
            >
              Try again
            </button>
          </EmptyState>
        ) : filtered.length === 0 ? (
          <EmptyState
            title="No customers found"
            hint={filtersActive
              ? 'Try clearing filters or searching a different name or city.'
              : 'Website shop customers will appear here after they register or checkout.'}
          >
            <button type="button" onClick={openAdd} className="px-6 py-2.5 rounded-lg text-white font-bold shadow" style={{ backgroundColor: FLARE_RED }}>Add Customer</button>
          </EmptyState>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wider">
                <tr>
                  <th className="px-6 py-3 text-left font-semibold">Customer</th>
                  <th className="px-6 py-3 text-left font-semibold">Contact</th>
                  <th className="px-6 py-3 text-left font-semibold">Source</th>
                  <th className="px-6 py-3 text-left font-semibold">Status</th>
                  <th className="px-6 py-3 text-right font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {paged.map((u, idx) => (
                  <tr key={u.id} className={`hover:bg-rose-50/30 ${idx % 2 === 1 ? 'bg-gray-50/60' : 'bg-white'}`}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold shadow-sm shrink-0" style={{ backgroundColor: FLARE_RED }}>{getInitials(u.name)}</div>
                        <div className="min-w-0">
                          <div className="font-bold text-gray-900">{u.name || '—'}</div>
                          <div className="text-xs text-gray-500 mt-0.5">{u.city || '—'}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-gray-700">{u.email || '—'}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{u.phone || 'No phone'}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border bg-indigo-50 text-indigo-800 border-indigo-100">
                        {u.source || '—'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${statusBadge(u.status)}`}>
                        {statusLabel(u.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right whitespace-nowrap">
                      <button type="button" onClick={() => openEdit(u)} className="px-3 py-1.5 rounded-lg text-sm font-semibold bg-gray-100 hover:bg-gray-200 text-gray-700 mr-2">Edit</button>
                      <button type="button" onClick={() => setBanning(u)} className="px-3 py-1.5 rounded-lg text-sm font-semibold bg-amber-50 hover:bg-amber-100 text-amber-800 mr-2">{u.status === 'banned' ? 'Unban' : 'Ban'}</button>
                      <button type="button" onClick={() => setDeleting(u)} className="px-3 py-1.5 rounded-lg text-sm font-semibold bg-red-50 hover:bg-red-100 text-red-700">Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading && filtered.length > 0 && (
          <div className="flex items-center justify-between p-4 border-t border-gray-100 bg-gray-50">
            <div className="text-sm text-gray-600">Total {filtered.length} customers · Page {currentPage} / {lastPage}</div>
            <div className="flex gap-2">
              <button type="button" disabled={currentPage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="px-3 py-1 bg-white border border-gray-300 rounded text-sm disabled:opacity-50">Prev</button>
              <button type="button" disabled={currentPage >= lastPage} onClick={() => setPage((p) => p + 1)} className="px-3 py-1 bg-white border border-gray-300 rounded text-sm disabled:opacity-50">Next</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
